import type { BaseTranslation } from '../../i18n-types';

const capture: BaseTranslation = {
  alertMessages: {
    addLocation: 'Add location',
    cannotDeleteCondition: 'Cannot delete condition',
    cannotDeleteLesion: 'Cannot delete lesion',
    cannotDeleteRash: 'Cannot delete rash',
    conditionHasImageRegistered:
      'This condition has information and cannot be deleted.',
    leave: 'Leave',
    lesionHasImageRegistered:
      'This lesion has information and cannot be deleted.',
    newClinicalAction: 'New clinical action?',
    newClinicalActionOrNewImage:
      'You can either make new clinical action or continue taking new images.',
    wantToLeave: 'Are you sure you want to leave this page?',
  },
  blurriness: {
    approvedDefault: 'Your image will be saved when you continue.',
    approvedDermoscopy:
      'Your dermoscopic image will be saved when you continue.',
    approvedTwoImages:
      'Two images (with/without flash) will be saved when you continue.',
    blurrinessBad: 'Is this image blurry?',
    blurrinessGood: 'Good job!',
    blurrinessUnchecked: 'Is the image sharp?',
    disapprovedDefault: 'Try cleaning the lens, holding the camera still.',
    disapprovedDermoscopy:
      'Zoom in on the lesion to check if the image is sharp. Retake if needed or continue with this image.',
    disapprovedTwoImages:
      'Retake the image, or continue if you believe the quality is sufficient.',
    error:
      "We couldn't assess the image quality this time, but you can still use it if it looks fine to you.",
    uncheckedDefault:
      'Check that the image is sharp. Retake if needed, or continue to save it.',
  },
  buttons: {
    addCondition: 'Add condition',
    addDifferentCondition: 'Add different condition',
    addImagesAction: 'Add images/action',
    addLesion: 'Add lesion',
    addLocation: 'Add location',
    addRash: 'Add rash',
    addSpecimenID: 'Add specimen ID manually',
    addSSN: 'Add information manually',
    allLesions: 'All lesions',
    dermoscope: 'Dermatoscope',
    done: 'Done',
    exitDemoMode: 'Exit Demo mode',
    findAddNewPatient: 'Find/add new patient',
    findOrAddPatient: 'Find or add patient',
    gotToHomePage: 'Go to home page',
    patientOverview: 'Patient overview',
    recentRegistrations: 'Recent consults',
    restartTheApp: 'Restart the app',
    retake: 'Retake',
  },
  camera: {
    addMoreImages: 'Add more images',
    closeUpImagesTitle: 'Close-up image{{nr:s}}',
    continue: 'Continue',
    deleteImage: {
      message:
        'Are you sure you want to delete this image?\n\nThis action cannot be undone.',
      title: 'Delete image?',
    },
    dermoscopyImagesTitle: 'Dermoscopic image{{nr:s}}',
    overviewImagesTitle: 'Overview image{{nr:s}}',
  },
  cameraPermissionScreen: {
    actionText: 'Continue',
    message: 'This feature requires camera access to function properly.',
    title: 'Camera access',
  },
  captureInstructions: {
    overviewImageTitle: 'Tips for high-quality overview images',
    dermoscopicImageTitle: 'Tips for high-quality dermoscopic images',
    closeUpImageTitle: 'Tips for high-quality close-up images',
    positionTheCameraAtCorrectDistance:
      'Position the camera at correct distance',
    ifCameraTooClose:
      "If the camera is <>too close<>, the image can be blurry and doesn't provide a good overview of the condition of the skin surrounding the lesion.",
    ifCameraTooFar:
      'If the camera is <>too far away<>, the details of the lesion can become blurry when zoomed in on a large desktop screen.',
    ensureTheImageIsSharpAndFocused: 'Ensure the image is sharp and focused',
    alwaysTapLesionAndWaitForYellowSquare:
      '<>Always tap the lesion on screen<> to focus camera in that spot. Wait for the yellow square to appear.',
    ensureLesionInCenter:
      'When possible make sure <>the lesion is in the center of the image<> indicated by white brackets.',
    zoomInToEnsureSharpness:
      'After capturing the image, <>zoom in<> to ensure the lesion is sharp—blurriness can be hard to detect on the small phone screen.',
    stillGettingPoorQualityImages: 'Still getting poor-quality images?',
    ensureEnoughLight: 'Make sure there is <>a lot of light in the room<>.',
    keepCameraStill:
      'Remember to <>keep the camera still<> when capturing the image.',
    cleanCameraLens:
      '<>Clean the camera lens<> if the images keep being blurry.',
    attachDermoscopeAndTurnItOn: 'Attach dermoscope and turn it on',
    pressDermliteButton:
      'Press the red button on the side of the dermoscope to turn it on.',
    onePressForPolarisedLight:
      'One press = polarized mode (recommended). <>Avoid double-pressing unless non-polarized mode is required.<>',
    pressMelascopeButton:
      'Press the black button on the side of the dermoscope to turn it on.',
    enablePolarisedLight:
      'Enable polarized light for optimal image quality if your dermoscope supports this.',
    useEnoughImmersionFluid: 'Use enough immersion fluid',
    applyImmersionFluidGenerously:
      'Apply a generous amount of alcohol or oil between the dermoscope and skin.',
    immersionFluidImportance:
      'It is especially important if there is <>make-up or dry, scaly skin<>.',
    alwaysUseImmersionFluidInNonPolarisedMode:
      '<>Always use immersion fluid (alcohol or oil)<> in non-polarized mode—without it, the images have little to no diagnostic value.',
    restDermoscopeDirectlyOnSkin:
      'Rest the dermoscope <>directly on the skin<>.',
    doNotPressDermoscopeTooHard:
      'Do not press too hard, as this may cause the blood in the small vessels around the lesion to drain away. Also, be careful not to press to hard when capturing elevated lesions. Always tap the lesion on screen to ensure proper focus.',
    alwaysTapLesion: 'Always tap the lesion on screen to ensure proper focus.',
    ensureContactWithSkin:
      'Make sure there is contact between the dermoscope and skin.',
    cleanCameraAndDermoscopeLens:
      '<>Clean the camera and dermoscope lenses<> if the images keep being blurry.',
    holdCameraFarEnough:
      'Hold the camera far enough to include the rash and surrounding healthy skin to provide the full context for diagnosis.',
    alwaysTapRashAndWaitForYellowSquare:
      "Always tap on the rash to focus the camera—wait for the yellow square to confirm it's in focus.",
    placeRashInCenter:
      'When possible place <>the rash in the center of the image<> indicated by white brackets.',
    alwaysTapRash: 'Always tap the rash on screen to ensure proper focus.',
    includeAsMuchSkinAsPossible: 'Include as much skin as possible',
    includeAsMuchSkinAsPossibleInImage:
      'Include as much skin as possible in the image e.g. both arms',
    moveCameraAwayForGoodImage: 'Move the camera away to get a good image',
    recommendedPolarizedLight: 'Recommended polarized light',
    nonPolarizedLight: 'Non-polarized light',
    enoughImmersionFluid: 'Enough\nimmersion fluid',
    tooLittleImmersionFluid: 'Too little\nimmersion fluid',
    footerText:
      'If you followed all of the above guidelines but keep getting poor quality images - contact our support at +45 44 40 82 45',
  },
  clinicalActions: {
    biopsy: 'Biopsy',
    biopsyActionText:
      '{biopsyTypeName|{Curretage: Curretage biopsy, Excision: Excision biopsy, Incision: Incision biopsy, Punch: Punch biopsy, Shave: Shave biopsy, *: Unspecified biopsy}}',
    clinicalAction: 'Clinical action',
    dismissal: 'No further action',
    followUpAssessment: 'Follow-up',
    newImageRegistration: 'New image registration',
    noAction: 'No action',
    notSpecified: 'Not specified',
    plannedBiopsy: 'Planned biopsy',
    referral: 'Referral',
    referralActionText:
      '{referralType|{physical: Physical referral, *: eConsult}}',
  },
  consentAlert: {
    clickHere: 'by pressing here.',
    consentAllOnSave: 'Consent to all and save',
    description: 'We would like your consent for the following',
    mandatory: 'Mandatory',
    save: 'Save my choices',
    title: 'Consent',
  },
  deleteUser: {
    accountDeletionWithinAMonth:
      'Account deletion will be done manually by the Melatech team within a month.',
    and: 'and',
    deleteAccount: 'Delete account',
    demrloopLearn: 'Dermloop Learn',
    dermloopCapture: 'Dermloop Capture',
    dermloopDesktop: 'Dermloop Desktop',
    ifYouRegret: 'if you regret',
    ifYouRegretYourAccountDeletion:
      'If you regret your account deletion, log into your account again before your account is deleted. You will be prompted if you wish to regret the account deletion.',
    registeredAccountDeletion:
      'We have registered your account deletion request. We will delete your account',
    requestDeletion: 'Do you want to request deletion of your account?',
    requestReceived: 'Request received',
    thisWillDeleteIn: 'Note that this will delete your account in',
    withinAMonth: 'Within a month',
  },
  demo: {
    alert: {
      button: {
        accept: 'Continue',
        exit: 'Exit Demo',
      },
      dataDeletionPolicy: 'The data you enter will be deleted after 24 hours.',
      demoModeConfirmation: 'You are in <>Dermloop Demo<> mode.',
      testPatientDisclaimer:
        'This is a <>TEST<> patient and the lesions and/or rashes you register will not be evaluated.',
      title: 'Dermloop Demo',
    },
    bannerTitle: 'Dermloop Demo',
    enteringModeAlert: {
      button: {
        accept: 'Continue',
        exit: 'Exit Demo',
      },
      dataResetPolicy: 'The data you enter will be reset after 24h.',
      entryNotice:
        'Please note that you are about to enter Dermloop Demo mode.',
      testPatientDisclaimer:
        'This means that this is a <>TEST<> patient and the registrations you make are not going to be evaluated or taken into consideration.',
      title: 'Dermloop Demo',
    },
    exitDemoMode: 'Exit Demo mode',
    testPatient: 'Test patient',
  },
  dermoscope: {
    includeSkin: 'Include as much skin as possible',
    includeSurrounding: 'Important to include surrounding skin',
    moveCameraAway: 'Move the camera away to get a good image',
    liveWarnings: {
      dermoscopeTurnedOff: 'The dermoscope is off',
      dermoscopeTurnedOffMessageDermlite:
        'Press the button once to turn it on with polarized light.',
      dermoscopeTurnedOffMessageMelascope: 'Turn it on to continue.',
      nonPolarisedLight: 'Polarized light not detected',
      nonPolarisedLightMessage:
        'Please ensure polarized light is enabled for optimal image quality.',
      readyToCapture: 'Ready to capture',
      readyToCaptureMessageDermlite:
        'Dermoscope is on and polarized light is active.',
      readyToCaptureMessageMelascope: 'Dermoscope is on.',
      generalInstruction: 'Reminder',
      generalInstructionMessage:
        'Ensure the dermoscope is turned on and in polarized light mode.',
    },
    imageWarnings: {
      dermoscopeNotAttached: 'Dermoscope is not attached',
      dermoscopeNotAttachedMessage:
        'Attach dermoscope and turn it on by clicking once on the red button on the side of the device.',
      dermoscopeTurnedOff: 'Dermoscope is turned off',
      dermoscopeTurnedOffMessage:
        'Turn on the dermoscope by clicking once on the red button on the side of the device.',
      nonPolarisedLight: 'Polarized light not detected',
      nonPolarisedLightMessage:
        'Enable polarized light for best image quality. Simply turn off the dermoscope and click only once on the red button to turn it on in polarized light mode.',
    },
  },
  descriptors: {
    allLesions: 'All growths',
    fetchingPatient: 'Fetching latest patient information',
    histologicalDiagnosis: 'Histological diagnosis',
    histopathologicalEvaluation: 'Histopathological evaluation',
    lastAccessed: 'Last accessed',
    lastClinicalAction: 'Last clinical action',
    lastRegistration: 'Last consult',
    lesionDoesntMatch: "Lesion doesn't match patient",
    lesionImageBaseline: 'Baseline',
    location: 'Location',
    medicalRecord: 'Medical record',
    newAction: 'New action',
    noLesion: 'No growth',
    noPatient: 'No patient',
    patientInfo: 'Patient info',
    patientOverview: 'Patient overview',
    specimenID: 'Specimen ID',
    teleEvaluation: 'Teledermatological evaluation',
    tentativeClinicalDiagnosis: 'Tentative clinical diagnosis',
  },
  desktopQrLogin: {
    buttonText: 'QR code on PC',
    scannerTitle: 'Scan QR code on PC',
    invalidCode: 'Invalid QR code',
  },
  dictation: {
    smartDictation: 'Smart dictation',
    stop: 'Stop',
    startDictation: 'Start dictation',
    stopDictation: 'Stop dictation',
    cleaningUp: 'Finishing up…',
    errorPrefix: 'Dictation error:',
    formatFailed: "That didn't quite come through - give it another go.",
    micPermissionTitle: "Let's turn on your microphone",
    micPermissionBody:
      "Smart dictation needs your microphone to work its magic. We'll save your draft before opening Settings, so you can pick right up where you left off.",
    micPermissionCancel: 'Cancel',
    micPermissionOpenSettings: 'Open Settings',
    micPermissionDenied: 'Microphone access is still off',
    audioSessionFailed: 'Could not activate audio session',
    recordingStartFailed: 'Failed to start recording',
    recordingUnknownError: 'Unknown recording error',
  },
  errorMessages: {
    anError: 'An error occurred',
    anErrorHappened: 'An error happened',
    cannotCreateLesion: 'Cannot create lesion',
    caseUnavailable:
      'There was an error, or the case you are trying to open no longer exists.',
    cannotCreateRash: 'Cannot create rash for non persisted patient',
    cannotDeleteLesion: 'Cannot delete lesion which has pending tasks.',
    cannotDeletePatientInProgress: 'Cannot delete patient which is in progress',
    creatingDetails: 'Problem creating the {type:string} details',
    creatingHealthRecordConsent: 'Problem creating health record consents',
    createHealthRecord:
      'Could not create health record, check your input and try again',
    creatingLesion: 'Problem creating the lesion',
    creatingRash: 'Problem creating the rash',
    creatingTriage: 'Cannot create consult',
    error: 'Error',
    errorAlternativeImage: 'Error occurred while uploading alternative image',
    errorOccurredWhileUpdating: 'An error occurred while updating',
    errorPrimaryImage: 'Error occurred while uploading primary image',
    errorWhileDeletingImage: 'Error occurred while deleting image',
    errorWhileGettingImage:
      'Error occurred while getting {imagetype:string} image',
    errorWhileUploading:
      'Error occurred while uploading {imagetype:string} image',
    gettingHealthRecord: 'Error occurred while getting patient',
    gettingUser: 'Error occurred while getting user',
    healthRecords: 'Error occurred for patients',
    inactiveLesion: 'Attempted to update inactive lesion.',
    missingConditionType: 'It seems that you are missing a condition type',
    newHealthRecord: 'An error happened while creating new patient',
    newPatient: 'A problem occurred while creating new patient',
    updatingPatient: 'A problem occurred while updating patient information',
    nonmatchigPatient: 'Could not update registration on non-matching patient',
    someError: 'An error occurred',
    somethingWrong: 'Something went wrong',
    unauthorized: 'Unauthorized',
    updatingDetails: 'Problem updating the {type:string} details',
    updatingLesion: 'Error occurred while updating lesion',
    updatingRash: 'Error occurred while updating rash',
    patientIsDead: 'The CPR you entered is from a dead person',
  },
  caseEditor: {
    editCase: 'Edit case',
    save: 'Save',
    saveAndSendTeleReferral: 'Save and send tele-referral',
    saveAndRecordActions: 'Save and record {count:number} action{{s}}',
    clinicalActions: 'Clinical actions',
    undo: 'Undo',
    cancelReferral: 'Cancel referral',
    keepReferral: 'Keep referral',
    cancelReferralPrompt: {
      title: 'Cancel this referral?',
      message: 'The referral is cancelled when you save the case.',
    },
    status: {
      awaitingAnswer: 'Awaiting answer',
      answered: 'Answered',
      cancelled: 'Cancelled',
      closed: 'Closed',
      recorded: 'Recorded',
    },
    saveAndCancelTeleReferral: 'Save and cancel tele-referral',
    saveAndRemoveActions: 'Save and remove {count:number} action{{s}}',
    saveAndApplyChanges: 'Save and apply {count:number} change{{s}}',
    removeAction: 'Remove {action:string}',
    removedOnSave: 'Removed when you save',
    cancelledOnSave: 'Cancelled when you save',
    sentOn: 'Sent {date:string}',
    sentOnBy: 'Sent {date:string} by {actor:string}',
    followUpMonths: '{count:number} month{{s}}',
    newClinicalAction: 'New clinical action',
    noNewAction: 'No new action',
    anotherAction: 'Another {action:string}',
    actionInstance: '{action:string} {number:number}',
    limitReason: {
      teleReferralAwaitingAnswer:
        'A tele-referral is already awaiting an answer. The specialist will see these changes.',
      alreadyConcluded: 'This case is already concluded.',
    },
    discardChanges: 'Discard changes',
    discardPrompt: {
      title: 'Discard changes?',
      message:
        'Your changes to this case have not been saved and will be lost.',
    },
    missingImages: {
      title: 'Required images are missing',
      message:
        'Some required images have not been taken yet. Take them now, or save the draft and add them later.',
      saveDraftAnyway: 'Save draft anyway',
    },
    staleEdit: {
      title: 'The case has changed',
      message:
        'A newer version of this case was saved after you started editing, so your changes could not be saved. They are still on screen. Reload the case to see the latest information.',
      reload: 'Reload',
    },
  },
  formRenderer: {
    photoRemoval: {
      deleteTitle: 'Delete this photo permanently?',
      deleteMessage:
        'It is removed from the case, including its history. This cannot be undone.',
      deleteRefused:
        'This photo can no longer be deleted, so it stays in the case.',
    },
    addAnotherImage: 'Add another image',
    addImages: 'Add images',
    cameraPermissionDenied:
      'Camera access is needed to capture images. Enable it in Settings.',
    cameraTip: 'Ensure good lighting and sharp focus',
    cervixCameraTip: 'Move close and hold still while capturing',
    cameraUnavailable: 'No camera is available on this device.',
    captureFailed: 'Could not capture the photo. Tap to try again.',
    uploadFailed: 'Upload failed',
    completeRegistration: 'Complete registration',
    conclude: 'Conclude',
    continueToNextImage: 'Continue to next image',
    continueToNextLocation: 'Continue to next location',
    deleteImage: 'Delete image',
    guideExamples: 'Examples of a good and a bad photo',
    help: {
      positionThePatient: 'Position the patient',
      tips: {
        avoidObstruction:
          "Make sure fingers or other objects don't block the view.",
        checkSharpnessAfterCapture:
          'After capturing, zoom in to check the image is sharp — blurriness can be hard to spot on a small screen.',
        ensureGoodLighting:
          'Make sure there is enough even light, avoiding harsh shadows.',
        fillTheFrame:
          'Move close enough that the area fills most of the frame.',
        holdSteady:
          'Hold the camera steady and wait for the image to come into focus before capturing.',
        mucoBuccalLeftFraming:
          'Ask the patient to open their mouth and gently retract the left cheek to expose the inner surface.',
        mucoBuccalRightFraming:
          'Ask the patient to open their mouth and gently retract the right cheek to expose the inner surface.',
        mucoLesionFraming:
          'Center the finding in the frame and move close enough to capture fine detail.',
        mucoLipsClosedFraming:
          'Ask the patient to gently close their lips in a relaxed, natural position.',
        mucoMouthFloorFraming:
          'Ask the patient to keep their tongue lifted to the roof of the mouth so the floor beneath it is fully visible.',
        mucoOcclusionGumsFraming:
          'Ask the patient to bite together gently so the upper and lower teeth and gums are both visible.',
        mucoPalateFraming:
          'Ask the patient to tilt their head back and open wide so the palate is clearly visible.',
        mucoTongueDorsumFraming:
          'Ask the patient to stick out their tongue and hold it flat so the top surface is fully visible.',
        mucoTongueLateralLeftFraming:
          'Ask the patient to turn their tongue to show the left side, using gauze if needed for grip.',
        mucoTongueLateralRightFraming:
          'Ask the patient to turn their tongue to show the right side, using gauze if needed for grip.',
        mucoTongueVentralFraming:
          'Ask the patient to lift their tongue to the roof of the mouth so the underside fills the frame.',
      },
      titles: {
        generic: 'Tips for taking a good photo',
        mucoBuccalLeft: 'Tips for capturing the inside of the left cheek',
        mucoBuccalRight: 'Tips for capturing the inside of the right cheek',
        mucoLesion: 'Tips for capturing a close-up of the finding',
        mucoLipsClosed: 'Tips for capturing closed lips',
        mucoMouthFloor: 'Tips for capturing the floor of mouth',
        mucoOcclusionGums: 'Tips for capturing the bite and gums',
        mucoPalate: 'Tips for capturing the roof of the mouth',
        mucoTongueDorsum: 'Tips for capturing the top of the tongue',
        mucoTongueLateralLeft: 'Tips for capturing the left side of the tongue',
        mucoTongueLateralRight:
          'Tips for capturing the right side of the tongue',
        mucoTongueVentral: 'Tips for capturing the underside of the tongue',
      },
    },
    imagePlural: 'images',
    imageSingular: 'image',
    imagesProgress: 'images',
    locationHelp: {
      title: 'How to mark a location',
      chooseSurface: 'Choose the surface',
      chooseSurfaceDescription:
        'Select the drawing that shows where the finding is located.',
      exploreModel: 'Rotate and zoom the model',
      exploreModelDescription:
        'Drag to rotate the model. Pinch to zoom in or out.',
      placeMarker: 'Place or move the marker',
      placeMarker2dDescription:
        'Tap the drawing to place the marker. Tap again to move it. The marker turns <>green<> when the location is selected.',
      placeMarker3dDescription:
        'Tap the model to place the marker. Tap again to move it. The marker turns <>green<> when the location is selected.',
      save: 'Save and continue',
      saveDescription:
        'Check that the green marker is in the right place, then save or continue to the next location.',
    },
    notePlaceholder: 'Add more details here...',
    markerOnThisSurface: 'Marker placed here',
    noMarkerOnThisSurface: 'No marker here',
    modelUnavailable:
      'The 3D model is not available in this version of the app',
    placeMarkerHint: 'Tap the illustration to set the marker',
    placeMarkerHint3d: 'Tap the model to set the marker',
    placeMarkerTitle: 'Tap the drawing to place the marker',
    placeMarkerTitle3d: 'Tap the model to place the marker',
    saveImagesToJournal: 'Save images in the Dermloop record',
    saveLocationToJournal: 'Save in the Dermloop record',
    selectAllThatApply: 'Select all that apply',
    sendTeleReferral: 'Send tele-referral',
    showLocation: 'Show location',
    takePhoto: 'Take photo',
    unsupportedWidget:
      'This form contains a question this app version cannot display. Please update the app.',
  },
  headerElements: {
    recentPatients: 'Recent patients',
  },
  images: {
    addImages: 'Add images',
    addImagesAndLocation: 'Add images and location',
    additionalImages: 'Additional images',
    takeImages: 'Take images',
  },
  loadingMessage: {
    creatingNewLesion: 'Creating new lesion',
    creatingNewRash: 'Creating new rash',
    uploadingImages: 'Uploading images... Please wait.',
    weAreRetrievingPatientInfo:
      "Retrieving patient information, this shouldn't take too long",
  },
  login: {
    title: 'Log in on this device',
    subtitle: 'Connect device to your computer session to get started',
    scanAndLogIn: 'Scan and log in',
    openQrScanner: 'Open QR scanner',
    noComputer: 'Working without a computer?',
    continueWithEmail: 'Continue with email',
    usePersonalPhone: 'Use personal phone to log in',
  },
  pathologyScanner: {
    addButton: 'Add number manually',
    helperText: 'Scan the barcode',
  },
  patientScanner: {
    addAddressManually: 'Add address manually',
    addressLabel: 'Address',
    addressSectionLabel: "Enter the patient's address and email",
    addressSectionTitle: 'Address and email',
    cityLabel: 'City',
    emailLabel: 'Email',
    inputOverviewDiscrepancyMessage:
      'The account number is already associated with a different patient. If the details do not match, please contact support at <>{supportPhone:string}<> for assistance.',
    inputOverviewLoadingTitle: 'Waiting for data...',
    inputOverviewTitle: 'Acc No. ⦁ {accountNumber:string}',
    inputOverviewTitleShort: 'No. ⦁ {accountNumber:string}',
    invalidEmail: 'Enter a valid email address',
    invalidPostalCode: 'Enter a valid postal code',
    phoneNumberLabel: "Enter the patient's phone number",
    postalCodeLabel: 'Postal code',
    requiredField: 'This field is required',
    scanAddress: 'Scan patient address',
  },
  patientCreator: {
    addPatientInfoManually: 'Add patient info manually',
    addAthenaManually: 'Add patient info manually',
    addEcwInfoManually: 'Add patient info manually',
    addEpicInfoManually: 'Add patient info manually',
    addSsnManually: 'Add SSN manually',
    addTomedoInfoManually: 'Add patient info manually',
    addQuincyInfoManually: 'Add patient info manually',
    addMedisoftwareInfoManually: 'Add patient info manually',
    errorMessage: {
      duplicateMrn: 'Duplicate MRN',
      invalidMrn: 'Invalid MRN',
      invalidSsn: 'Invalid CPR',
    },
    labels: {
      birthDate: 'Date of birth',
      firstName: 'First name',
      gender: 'Gender',
      lastName: 'Last name',
      mrn: 'Account number',
      transgender: 'Transgender',
    },
    scanPatientInfo: 'Scan patient info',
    scanAthenaInfo: 'Scan patient info',
    scanEcwInfo: 'Scan patient info',
    scanEpicInfo: 'Scan patient info',
    scanSsnOrBarcode: 'Scan CPR or barcode',
    scanTomedoInfo: 'Scan patient info',
    scanQuincyInfo: 'Scan patient info',
    scanMedisoftwareInfo: 'Scan patient info',
  },
  plaintext: {
    additionsJoinSameOrder: 'Anything you add below joins the same order.',
    completedEntry: 'Your consult has been completed',
    completedPathology:
      'Your registration has been sent for histopathological evaluation',
    completedSteps: 'Completed steps',
    completedTeleReferral:
      'Your eConsult has been submitted for teledermatological evaluation',
    howToContinueFromHere: 'How would you like to continue from here?',
    lastAccessed: 'Last accessed',
    participationInTeleProject: 'Participation in the teledermatology project',
    recentRegistrations: 'Your recent consults will be displayed here',
    saveAsDraft: 'Save as draft',
    selectExistingCondition: 'Select an existing condition',
    selectExistingLesion: 'Select an existing lesion',
    selectExistingRash: 'Select an existing rash',
    selectSpecialityToRegister:
      'Please select the speciality you need a consult on',
    selectTypeConditionToRegister:
      'Please select the type of condition you need a consult on',
    signOrderInEpicToSubmit: 'Sign the order in Epic to submit this eConsult',
    unit: '{unit:string}',
    youCanEditAndFinalizeLater: 'You can edit and finalize later',
  },
  scanner: {
    input: {
      confirmPatient: 'Confirm patient',
      fillingInInfo: 'Enter information',
      infoEntered: 'Information entered',
      invalidInfo: 'Invalid information',
      patientFound: 'Patient found',
    },
  },
  searchResults: {
    startTypingToSeeMoreResults: 'Start typing to see more results',
  },
  conditionOverview: {
    footerButton: {
      goToRegistration: 'Back',
      makeNewRegistration: 'Add images/action',
    },
    thumbnailImage: {
      closeUp: 'Close-up',
      dermoscopy: 'Dermoscopy',
      overview: 'Overview',
    },
  },
  ssnScanner: {
    inputOverviewLoadingTitle: 'Waiting for data...',
    inputOverviewTitle: 'CPR ⦁ {ssn:string}',
    overviewValues: {
      birthDate: 'Date of birth',
      gender: 'Gender',
      firstName: 'First name',
      lastName: 'Last name',
      isNameProtected: '*****',
    },
  },
  secondImageWarning: {
    title: 'Hold on - is it the same lesion?',
    message:
      'More images of <>the same lesion<> are fine - but <>a different lesion<> deserves its own case.',
    note: 'Finish this case, then add the new lesion from the lesion overview.',
    differentLesion: 'No, different lesion',
    sameLesion: 'Yes, same lesion',
  },
  settings: {
    account: 'Account',
    app: 'App',
    change: 'Change',
    clinic: 'Site',
    email: 'Email',
    emailLocked: 'This email cannot be changed here. Contact support.',
    legal: 'Legal',
    licensesIntro:
      'Dermloop Capture is built on open source software. These are the packages it ships and their licences.',
    name: 'Name',
    openSourceLicenses: 'Open source licences',
    packageCount: '{count:number} package{{s}}',
    support: 'Support',
    version: 'Version',
  },
  statuses: {
    diagnosed: 'Diagnosed',
    noDiagnosis: 'No diagnosis',
    noRegistered: 'No registered diagnosis',
    noRegisteredLesion: 'No registered lesion',
    pendingDiagnostic: 'Pending diagnosis',
    undiagnosable: 'Undiagnosable',
  },
  statusMessages: {
    noAccesToMedicalUnits: "You don't have access to any sites.",
    noMatchingPatient: 'No matching patient',
    ressourceNotFound: 'Resource not found',
    scanHealthInsuranceCard: 'Scan Health Insurance Card',
    scanSSNOrBarcode: 'Scan CPR or barcode',
    selectLesionLocationBeforeContinuing:
      'Before continuing, we recommend selecting lesion location',
    selectRashLocationBeforeContinuing:
      'Before continuing, we recommend selecting rash location',
    SSN: 'CPR',
    tapToSelectLesionLocation: 'Tap to select lesion location',
    tapToSelectRashLocation: 'Tap to select rash location',
    whenYouCaptureACondition:
      'When you capture a condition, it will be displayed here',
    whenYouCaptureALesion:
      'When you capture a lesion, it will be displayed here',
    whenYouCaptureARash:
      'When you capture a rash it will be displayed here as a list item',
    attachDermatoscopeForDermoscopicImage:
      'Attach dermoscope to capture dermoscopic image',
    detachDermatoscopeForOverviewImage:
      'Detach dermoscope to capture overview image',
    detachDermatoscopeForCloseUpImage:
      'Detach dermoscope to capture close-up image',
    detachDermatoscopeToScanAthenaInfo:
      'Detach dermoscope to scan patient info',
    detachDermatoscopeToScanPatientInfo:
      'Detach dermoscope to scan patient info',
    detachDermatoscopeToScanEcwInfo: 'Detach dermoscope to scan patient info',
    detachDermatoscopeToScanEpicInfo: 'Detach dermoscope to scan patient info',
    detachDermatoscopeToScanSSN: 'Detach dermoscope to scan CPR number',
    detachDermatoscopeToScanPathologyInfo: 'Detach dermoscope to scan barcode',
    detachDermatoscopeToScanQRCode: 'Detach dermoscope to scan QR code',
    detachDermatoscopeToScanTomedoInfo:
      'Detach dermoscope to scan patient info',
    detachDermatoscopeToScanQuincyInfo:
      'Detach dermoscope to scan patient info',
    detachDermatoscopeToScanMedisoftwareInfo:
      'Detach dermoscope to scan patient info',
  },
  triageRegistration: {
    header: {
      viewImagesOrRecord: 'View images/record',
    },
    loadingMessage: {
      deleting: 'Deleting...',
    },
    missingInfoAlert: {
      closeUpImage: {
        button: 'Add close-up image',
        step1: 'Remove the dermatoscope',
        step2: 'Take a close-up image by moving the camera away',
        title: 'Close-up image missing',
      },
      dermoscopicImage: {
        button: 'Add dermoscopic image',
        step1: 'Attach the dermatoscope',
        step2: 'Take a dermoscopic image of the lesion',
        title: 'Dermoscopic image missing',
      },
      overviewImageLesion: {
        button: 'Add overview image',
        step1: 'Remove the dermatoscope',
        step2: 'Take a overview image of the lesion and the surrounding skin',
        title: 'Overview image missing',
      },
      overviewImageRash: {
        button: 'Add overview image',
        step1: 'Remove the dermatoscope',
        step2:
          'Take an overview image and include as much of the skin as possible',
        title: 'Overview image missing',
      },
    },
  },
  immersionFluidGuide: {
    title: 'Remember immersion fluid',
    closeLabel: 'Close guide',
    applyAlcohol: 'Apply alcohol as immersion fluid on the skin',
    applyAlcoholDesc:
      'This minimizes glare and makes the deeper skin structures visible for a precise analysis.',
    step1: 'Wipe the <>lens<> on the dermatoscope with an alcohol swab',
    step2: 'Clean the skin on and around the <>lesion<>',
    step3: 'Place the dermatoscope while the skin is <>still wet<>',
    disclaimer:
      'You are seeing this guide because this is your first registration, or because the dermatologist reviewing your latest case noted that the image quality was affected by insufficient immersion fluid. Following these steps ensures your dermatologist gets the clearest possible view.',
    understood: 'Understood',
  },
  warnings: {
    checkInternetConnection: 'Please check your internet connection',
    credentialsExpired: 'Your credentials have expired',
    reenterPassworf: 'Please reenter your password to continue',
    requestNotComplete: 'We could not complete your request',
  },
};

export default capture;
